import { observer } from 'mobx-react-lite'
import {
  ListItemIcon,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'

import { AppSearchBar } from 'src/component/common/analog/molecule/AppSearchBar'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { type MouseEvent, useState } from 'react'

export type ProjectStatusFilter =
  | 'ALL'
  | 'ACTIVE'
  | 'PLANNED'
  | 'FINISHED'

export type ProjectSortOption =
  | 'NONE'
  | 'NEWEST'
  | 'OLDEST'

type ProjectListToolbarProps = {
  selectedFilter: ProjectStatusFilter
  selectedSort: ProjectSortOption

  onSearchChange: (value: string) => void
  onFilterChange: (value: ProjectStatusFilter) => void
  onSortChange: (value: ProjectSortOption) => void
}

const FILTER_OPTIONS: ProjectStatusFilter[] = [
  'ALL',
  'ACTIVE',
  'PLANNED',
  'FINISHED',
]

export const ProjectListToolbar = observer(({
  selectedFilter,
  selectedSort,
  onSearchChange,
  onFilterChange,
  onSortChange,
}: ProjectListToolbarProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  // For drop down badges sort and filter
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)

  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null)

  const selectedFilterLabel = selectedFilter === 'ALL'
    ? projectBuilderTranslation.allProjects()
    : selectedFilter === 'ACTIVE'
      ? projectBuilderTranslation.active()
      : selectedFilter === 'PLANNED'
        ? projectBuilderTranslation.planned()
        : projectBuilderTranslation.finished()

  const selectedSortLabel = selectedSort === 'NONE'
    ? projectBuilderTranslation.sortBy()
    : selectedSort === 'NEWEST'
      ? projectBuilderTranslation.newest()
      : projectBuilderTranslation.oldest()

  const sortOptions: Array<{ value: ProjectSortOption, label: string }> = [
    { value: 'NONE', label: projectBuilderTranslation.sortBy() },
    { value: 'NEWEST', label: projectBuilderTranslation.newest() },
    { value: 'OLDEST', label: projectBuilderTranslation.oldest() },
  ]

  return (
    <AppBox sx={styles.root}>
      <AppSearchBar
        onChange={e => onSearchChange(e.target.value)}
        sx={styles.searchBar}
      />

      <AppBox sx={styles.bottomRow}>

        <AppBox>
          <AppBox
            component='button'
            type='button'
            sx={styles.dropdownButton}
            onClick={(e: MouseEvent<HTMLElement>) => setFilterAnchor(e.currentTarget)}
            aria-controls={filterAnchor ? 'project-filter-menu' : undefined}
            aria-expanded={filterAnchor ? 'true' : undefined}
            aria-haspopup='menu'
          >
            <Typography sx={styles.dropdownText}>
              {selectedFilterLabel}
            </Typography>
            {filterAnchor
              ? <KeyboardArrowUpRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />
              : <KeyboardArrowDownRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />}
          </AppBox>

          <Menu
            anchorEl={filterAnchor}
            id='project-filter-menu'
            open={Boolean(filterAnchor)}
            onClose={() => setFilterAnchor(null)}
          >
            <Typography sx={styles.menuSectionTitle}>Filter</Typography>
            {FILTER_OPTIONS.map(filter => (
              <MenuItem
                key={filter}
                onClick={() => {
                  onFilterChange(filter)
                  setFilterAnchor(null)
                }}
                sx={styles.menuItem}
              >
                <ListItemIcon>{selectedFilter === filter && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}</ListItemIcon>
                {filter === 'ALL'
                  ? projectBuilderTranslation.allProjects()
                  : filter === 'ACTIVE'
                    ? projectBuilderTranslation.active()
                    : filter === 'PLANNED'
                      ? projectBuilderTranslation.planned()
                      : projectBuilderTranslation.finished()}
              </MenuItem>
            ))}
          </Menu>

        </AppBox>

        <AppBox>
          <AppBox
            component='button'
            type='button'
            sx={styles.dropdownButton}
            onClick={(e: MouseEvent<HTMLElement>) => setSortAnchor(e.currentTarget)}
            aria-controls={sortAnchor ? 'project-sort-menu' : undefined}
            aria-expanded={sortAnchor ? 'true' : undefined}
            aria-haspopup='menu'
          >
            <Typography sx={styles.dropdownText}>
              {selectedSortLabel}
            </Typography>
            {sortAnchor
              ? <KeyboardArrowUpRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />
              : <KeyboardArrowDownRoundedIcon fontSize='small' sx={{ color: 'inherit' }} />}
          </AppBox>

          <Menu
            anchorEl={sortAnchor}
            id='project-sort-menu'
            open={Boolean(sortAnchor)}
            onClose={() => setSortAnchor(null)}
          >
            <Typography sx={styles.menuSectionTitle}>{projectBuilderTranslation.sortBy()}</Typography>
            {sortOptions.map(sort => (
              <MenuItem
                key={sort.value}
                onClick={() => {
                  onSortChange(sort.value)
                  setSortAnchor(null)
                }}
                sx={styles.menuItem}
              >
                <ListItemIcon>{selectedSort === sort.value && <CheckIcon fontSize="small" sx={{ color: 'text.primary' }} />}</ListItemIcon>
                {sort.label}
              </MenuItem>
            ))}
          </Menu>

        </AppBox>

      </AppBox>
    </AppBox>
  )
})

const styles = {
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    flexWrap: 'nowrap',
  },
  searchBar: {
    flex: 1,
    minWidth: 0,
    border: 0,
    backgroundColor: ColorTokens.primary10,
    input: {
      fontSize: '12px',
    },
    '&:hover': {
      border: 0,
    },
    '&.Mui-focused': {
      border: 0,
    },
    '&.Mui-focused:hover': {
      border: 0,
    },
    '&:active': {
      border: 0,
    },
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'nowrap',
    flexShrink: 0,
  },
  dropdownButton: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    border: '1px solid',
    borderColor: 'primary.main',
    backgroundColor: 'primary.main',
    color: 'background.default',
    padding: '5px 10px',
    borderRadius: '10px',
  },
  menuSectionTitle: {
    color: 'text.secondary',
    padding: '0.5rem 1rem',
    fontSize: 12,
  },
  menuItem: {
    fontSize: 12,
    '& .MuiListItemIcon-root': {
      minWidth: 24,
    },
  },
  dropdownText: {
    fontSize: 12,
    fontWeight: 400,
    color: 'inherit',
  },
} as const